import "./UpdateGstDetailsModal.scss";

import React, { useEffect, useState } from "react";

import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Typography,
} from "antd";
import PropTypes from "prop-types";

import { updateUserDetails } from "../Pages/api";
import { showAppError } from "../Utils/error";

const { Option } = Select;

const GST_PRESET_OPTIONS = [
  { label: "0", value: "0" },
  { label: "5", value: "5" },
  { label: "12", value: "12" },
  { label: "18", value: "18" },
  { label: "28", value: "28" },
  { label: "Custom", value: "custom" },
];

const SAC_REGEX = /^\d{6}$/;

const GST_SLAB_DEFAULT_DISPLAY = "18%";
const GST_HSN_DEFAULT_DISPLAY = "Did not set this value";

const getCurrentGstSlabDisplay = (user) => {
  if (
    !user ||
    !Object.prototype.hasOwnProperty.call(user, "gstSlabPercentage")
  ) {
    return GST_SLAB_DEFAULT_DISPLAY;
  }
  const v = user.gstSlabPercentage;
  if (v === null || v === undefined || v === "") {
    return GST_SLAB_DEFAULT_DISPLAY;
  }
  return `${v}%`;
};

const getCurrentGstHsnDisplay = (user) => {
  if (!user || !Object.prototype.hasOwnProperty.call(user, "gstHsnCode")) {
    return GST_HSN_DEFAULT_DISPLAY;
  }
  const v = user.gstHsnCode;
  if (v === null || v === undefined || String(v).trim() === "") {
    return GST_HSN_DEFAULT_DISPLAY;
  }
  return String(v).trim();
};

const UpdateGstDetailsModal = ({
  visible,
  onClose,
  creatorId,
  creatorName,
  user,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const handleSubmit = async () => {
    if (!creatorId) {
      message.error("Missing creator. Close the modal and try again.");
      return;
    }

    try {
      await form.validateFields();
    } catch {
      return;
    }

    const values = form.getFieldsValue();
    const gstSlabPercentage =
      values.gstPreset === "custom"
        ? Number(values.customGstValue)
        : Number(values.gstPreset);
    const sac = (values.sacNumber || "").trim();

    setSubmitting(true);
    try {
      const resp = await updateUserDetails({
        creatorId,
        gstSlabPercentage,
        gstHsnCode: sac,
      });
      if (resp?.code === 0) {
        message.success("GST details updated");
        onSuccess?.();
        onClose?.();
        form.resetFields();
      } else {
        showAppError(resp);
      }
    } catch (err) {
      showAppError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={() => {
        form.resetFields();
        onClose?.();
      }}
      footer={null}
      destroyOnClose
      title={creatorName ? `Update GST — ${creatorName}` : "Update GST details"}
      width={480}
    >
      <div className="update-gst-details-modal">
        <Space
          direction="vertical"
          size="small"
          style={{ width: "100%", marginBottom: 16 }}
        >
          <Typography.Text type="secondary">
            Current GST value:{" "}
            <Typography.Text strong>
              {getCurrentGstSlabDisplay(user)}
            </Typography.Text>
          </Typography.Text>
          <Typography.Text type="secondary">
            Current SAC number:{" "}
            <Typography.Text strong>
              {getCurrentGstHsnDisplay(user)}
            </Typography.Text>
          </Typography.Text>
        </Space>

        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          className="update-gst-details-modal__form"
        >
          <Form.Item
            name="gstPreset"
            label="GST value"
            rules={[{ required: true, message: "Please select a GST value" }]}
          >
            <Select placeholder="Select GST value">
              {GST_PRESET_OPTIONS.map((o) => (
                <Option key={o.value} value={o.value}>
                  {o.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.gstPreset !== cur.gstPreset}
          >
            {({ getFieldValue }) =>
              getFieldValue("gstPreset") === "custom" ? (
                <Form.Item
                  name="customGstValue"
                  label="Custom GST value"
                  dependencies={["gstPreset"]}
                  rules={[
                    {
                      validator(_, value) {
                        if (getFieldValue("gstPreset") !== "custom") {
                          return Promise.resolve();
                        }
                        if (
                          value === undefined ||
                          value === null ||
                          value === ""
                        ) {
                          return Promise.reject(
                            new Error("Please enter a custom GST value")
                          );
                        }
                        const n = Number(value);
                        if (Number.isNaN(n) || n < 0 || n > 100) {
                          return Promise.reject(
                            new Error(
                              "Custom GST value must be between 0 and 100"
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    style={{ width: "100%" }}
                    placeholder="0–100"
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item
            name="sacNumber"
            label="SAC number"
            rules={[
              { required: true, message: "Please enter SAC number" },
              {
                pattern: SAC_REGEX,
                message: "SAC number must be exactly 6 digits",
              },
            ]}
          >
            <Input maxLength={6} placeholder="6 digits" autoComplete="off" />
          </Form.Item>
        </Form>

        <div className="update-gst-details-modal__disclaimer">
          <div className="update-gst-details-modal__disclaimer-title">
            Disclaimer
          </div>
          <div className="update-gst-details-modal__disclaimer-text">
            This change will be effective for all new services created from
            today onward.
          </div>
        </div>

        <Space style={{ marginTop: 4 }}>
          <Button
            type="primary"
            className="siteBtn accentBtnV1"
            loading={submitting}
            onClick={handleSubmit}
          >
            Save
          </Button>
          <Button
            onClick={() => {
              form.resetFields();
              onClose?.();
            }}
          >
            Cancel
          </Button>
        </Space>
      </div>
    </Modal>
  );
};

UpdateGstDetailsModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  creatorId: PropTypes.string,
  creatorName: PropTypes.string,
  user: PropTypes.shape({
    gstSlabPercentage: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    gstHsnCode: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
};

export default UpdateGstDetailsModal;
